import { useCallback, useEffect, useRef } from 'react';
import {
  useMediaStore,
  useObjectDetectionStore,
  type DetectedObjectResults,
  type MediaItem,
} from '@brightmls/nextgen.lae.mediacenter';
import { useService } from '../../../../contexts/ServiceContext';
import { batchProcess } from '../../../../components/edit/media/utils/batchProcess';
import {
  loadDetectedObjects,
  persistDetectedObjects,
  clearDetectedObjects,
  evictStaleEntries,
} from './objectDetectionPersistence';

interface UseObjectDetectionFlowOptions {
  listingKey: string;
  objectDetectionEnabled: boolean;
}

export function useObjectDetectionFlow({
  listingKey,
  objectDetectionEnabled,
}: UseObjectDetectionFlowOptions) {
  const items = useMediaStore((state) => state.items);
  const {
    hydrate,
    addMultipleDetectedObjects,
    clearDetectedObjects: clearStore,
    startScanning,
    stopScanning,
    setScanProgress,
  } = useObjectDetectionStore();

  const { generateObjectsForImage } = useService();

  const processedKeysRef = useRef<Set<string>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const hasHydratedRef = useRef(false);

  // On mount: evict stale entries + hydrate from localStorage
  useEffect(() => {
    evictStaleEntries();

    if (!listingKey || !objectDetectionEnabled) return;

    const cached = loadDetectedObjects(listingKey);
    if (cached) {
      hydrate(cached.results);
      cached.processedPhotoKeys.forEach((key) =>
        processedKeysRef.current.add(key),
      );
      hasHydratedRef.current = true;
    }
  }, [listingKey, objectDetectionEnabled, hydrate]);

  // Watch items for new _aiResults.detectedObjects
  useEffect(() => {
    if (!objectDetectionEnabled || !listingKey) return;

    // Filter items: photo category, not processing, has MEDIA_URL, has detectedObjects, not already processed
    const newItems = items.filter((item) => {
      if (item._isProcessing) return false;
      if (!item.MEDIA_URL) return false;
      if (!item._aiResults?.detectedObjects) return false;
      if (processedKeysRef.current.has(item.MED_VW_KEY)) return false;
      // Only photos
      const cat = item.MEDIA_CATEGORY?.toLowerCase();
      if (cat !== 'photo') return false;
      return true;
    });

    if (newItems.length === 0) return;

    // Debounce aggregation
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      // Aggregate detectedObjects from all new items
      const aggregated: DetectedObjectResults = {};

      newItems.forEach((item) => {
        const objects = item._aiResults?.detectedObjects;
        if (!objects) return;

        Object.entries(objects).forEach(([label, detections]) => {
          if (!aggregated[label]) {
            aggregated[label] = [];
          }

          detections.forEach((detection) => {
            // Deduplicate by fieldKey
            const exists = aggregated[label].some(
              (d) => d.fieldKey === detection.fieldKey,
            );
            if (!exists) {
              aggregated[label].push({
                label: detection.label,
                fieldKey: detection.fieldKey,
                fieldName: detection.fieldName,
                confidence: detection.confidence,
              });
            }
          });
        });

        // Mark as processed
        processedKeysRef.current.add(item.MED_VW_KEY);
      });

      // Add to store
      addMultipleDetectedObjects(aggregated);

      // Persist to localStorage
      const currentResults = useObjectDetectionStore.getState().detectedObjects;
      persistDetectedObjects(listingKey, currentResults, [
        ...processedKeysRef.current,
      ]);
    }, 2000);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [items, objectDetectionEnabled, listingKey, addMultipleDetectedObjects]);

  // Reanalyze handler
  const reanalyze = useCallback(async () => {
    if (!listingKey) return;

    // Abort any in-progress scan
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    // Clear store + localStorage
    clearStore();
    clearDetectedObjects(listingKey);
    processedKeysRef.current.clear();

    // Gather all completed photo items with a URL (prefer MEDIA_URL_MEDIUM for AI)
    const photos = items.filter((item) => {
      const cat = item.MEDIA_CATEGORY?.toLowerCase();
      return (
        cat === 'photo' &&
        !item._isProcessing &&
        !!(item.MEDIA_URL_MEDIUM || item.MEDIA_URL)
      );
    });

    if (photos.length === 0) {
      return;
    }

    startScanning();
    setScanProgress(0, photos.length);

    try {
      const results = await batchProcess<
        MediaItem,
        DetectedObjectResults | undefined
      >(
        photos,
        async (photo) => {
          // Prefer medium URL (smaller, faster for AI) matching V1 behavior
          const imageUrl = (photo.MEDIA_URL_MEDIUM ||
            photo.MEDIA_URL) as string;
          const result = await generateObjectsForImage(listingKey, imageUrl);

          // generateObjectsForImage returns field-match results at runtime:
          // { [label]: [{ score, object_label, fieldKey, fieldName }] }
          // (TypeScript type is inaccurate — it declares { description, url })
          if (!result || typeof result !== 'object') return undefined;

          const mapped: DetectedObjectResults = {};
          Object.entries(result as unknown as Record<string, any[]>).forEach(
            ([label, detections]) => {
              if (!Array.isArray(detections)) return;
              mapped[label] = detections.map((d: any) => ({
                label: d.label || label,
                fieldKey: d.fieldKey,
                fieldName: d.fieldName,
                confidence: d.score ?? d.confidence,
              }));
            },
          );
          return mapped;
        },
        {
          batchSize: 5,
          maxRetries: 2,
          onProgress: (done, total) => {
            setScanProgress(done, total);
          },
        },
        abortRef.current.signal,
      );

      // Aggregate all results
      const aggregated: DetectedObjectResults = {};
      results.forEach((result) => {
        if (!result) return;
        Object.entries(result).forEach(([label, detections]) => {
          if (!aggregated[label]) {
            aggregated[label] = [];
          }
          detections.forEach((detection) => {
            const exists = aggregated[label].some(
              (d) => d.fieldKey === detection.fieldKey,
            );
            if (!exists) {
              aggregated[label].push(detection);
            }
          });
        });
      });

      hydrate(aggregated);

      // Mark all photos as processed
      photos.forEach((p) => processedKeysRef.current.add(p.MED_VW_KEY));

      // Persist
      persistDetectedObjects(listingKey, aggregated, [
        ...processedKeysRef.current,
      ]);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('Object detection reanalyze failed:', err);
    } finally {
      stopScanning();
    }
  }, [
    listingKey,
    items,
    clearStore,
    startScanning,
    stopScanning,
    setScanProgress,
    hydrate,
    generateObjectsForImage,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return { reanalyze };
}
